import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Collapse, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DownloadIcon from '@mui/icons-material/Download';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuditEvent, AuditFilters, AuditLogService } from '../../services/auditLog.service';
import { SearchableSelect } from '../common/SearchableSelect';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { humanizeEnum } from '../../utils/format';

const cell = (basis: number) => ({ flex: `1 1 ${basis}px`, maxWidth: basis + 120, minWidth: 0 });

const formatWhen = (iso: string): string =>
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Dhaka', dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(iso));

type Diff = Record<string, { old: string | null; new: string | null }>;
const parseDiff = (json: string | null): Diff => {
  try { return json ? (JSON.parse(json) as Diff) : {}; } catch { return {}; }
};

// Defensive: a flat (non old/new) value renders as the new value instead of throwing.
const diffSide = (v: unknown, side: 'old' | 'new'): string => {
  if (v !== null && typeof v === 'object' && ('old' in v || 'new' in v)) {
    const x = (v as Record<string, unknown>)[side];
    return x === null || x === undefined ? '-' : String(x);
  }
  if (side === 'old') return '-';
  return v === null || v === undefined ? '-' : String(v);
};

const Row: React.FC<{ e: AuditEvent }> = ({ e }) => {
  const [open, setOpen] = useState(false);
  const diff = parseDiff(e.changedFields);
  const keys = Object.keys(diff);
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton data-help="accounting.auditLog.action.expand" size="small" aria-label="Show changes" onClick={() => setOpen(o => !o)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{formatWhen(e.occurredAt)}</TableCell>
        <TableCell>{humanizeEnum(e.entityType)}</TableCell>
        <TableCell>{humanizeEnum(e.action)}</TableCell>
        <TableCell>{e.documentRef ?? '-'}</TableCell>
        <TableCell>{e.userName ?? (e.userId ? 'Unknown user' : 'System')}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, border: open ? undefined : 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ m: 1 }}>
              {keys.length === 0 ? <Typography variant="body2">No tracked fields changed.</Typography> : (
                <Table size="small">
                  <TableHead><TableRow><TableCell data-help="accounting.auditLog.change.col.field">Field</TableCell><TableCell data-help="accounting.auditLog.change.col.old">Old</TableCell><TableCell data-help="accounting.auditLog.change.col.new">New</TableCell></TableRow></TableHead>
                  <TableBody>
                    {keys.map(k => (
                      <TableRow key={k}>
                        <TableCell>{humanizeEnum(k)}</TableCell>
                        <TableCell>{diffSide(diff[k], 'old')}</TableCell>
                        <TableCell>{diffSide(diff[k], 'new')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AuditLog: React.FC = () => {
  const { selectedInstitutionId } = useAuth();
  const toast = useToast();
  const [types, setTypes] = useState<string[]>([]);
  const [draft, setDraft] = useState<AuditFilters>({});
  const [applied, setApplied] = useState<AuditFilters>({});
  const [user, setUser] = useState<EmployeeOption | null>(null);
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedInstitutionId) return;
    AuditLogService.entityTypes(selectedInstitutionId).then(setTypes).catch(err => toast.showApiError(err, 'Failed to load entity types'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitutionId]);

  const load = useCallback(async () => {
    if (!selectedInstitutionId) return;
    setLoading(true);
    try {
      const res = await AuditLogService.list(selectedInstitutionId, applied, page, size);
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (err) {
      toast.showApiError(err, 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstitutionId, applied, page, size]);

  useEffect(() => { void load(); }, [load]);

  const apply = () => { setPage(0); setApplied({ ...draft, userId: user?.id }); };

  const exportXlsx = async () => {
    if (!selectedInstitutionId) return;
    try { await AuditLogService.exportXlsx(selectedInstitutionId, applied); }
    catch (err) { toast.showApiError(err, 'Export failed'); }
  };

  return (
    <Box data-help-page="accounting.auditLog.page" sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Audit Log</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only trail of changes to money-bearing records and access settings. Times are Asia/Dhaka.
      </Typography>
      <Box data-help-section="accounting.auditLog.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <Box sx={cell(200)}>
          <SearchableSelect<string> data-help="accounting.auditLog.entity" label="Entity type" size="small" options={types} value={draft.entityType ?? ''}
            getOptionId={t => t} getOptionLabel={t => humanizeEnum(t)}
            onChange={id => setDraft(d => ({ ...d, entityType: id }))} />
        </Box>
        <Box sx={cell(220)}>
          <EmployeeAutocomplete data-help="accounting.auditLog.user" label="User" value={user} onChange={v => setUser(v)} />
        </Box>
        <Box sx={cell(150)}>
          <TextField data-help="accounting.auditLog.from" label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
            value={draft.from ?? ''} onChange={e => setDraft(d => ({ ...d, from: e.target.value }))} />
        </Box>
        <Box sx={cell(150)}>
          <TextField data-help="accounting.auditLog.to" label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }}
            value={draft.to ?? ''} onChange={e => setDraft(d => ({ ...d, to: e.target.value }))} />
        </Box>
        <Box sx={cell(180)}>
          <TextField data-help="accounting.auditLog.documentRef" label="Document ref" size="small" fullWidth value={draft.documentRef ?? ''}
            onChange={e => setDraft(d => ({ ...d, documentRef: e.target.value }))} />
        </Box>
        <Button data-help="accounting.auditLog.apply" variant="contained" onClick={apply}>Apply</Button>
        <Button data-help="accounting.auditLog.export" variant="outlined" startIcon={<DownloadIcon />} onClick={exportXlsx}>Export XLSX</Button>
      </Box>
      <TableContainer data-help-section="accounting.auditLog.table" component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell data-help="accounting.auditLog.col.when">When</TableCell><TableCell data-help="accounting.auditLog.col.entity">Entity</TableCell><TableCell data-help="accounting.auditLog.col.action">Action</TableCell>
              <TableCell data-help="accounting.auditLog.col.document">Document</TableCell><TableCell data-help="accounting.auditLog.col.user">User</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(e => <Row key={e.id} e={e} />)}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6}>No audit events for these filters.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination data-help="accounting.auditLog.paging" component="div" count={total} page={page} rowsPerPage={size}
          rowsPerPageOptions={[25, 50, 100]} onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }} />
      </TableContainer>
    </Box>
  );
};

export default AuditLog;
